import React, { useEffect, useState } from "react";
import MuiBox from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Typography from "@mui/material/Typography";
import DescriptionIcon from "@mui/icons-material/Description";
import { styled } from '@mui/material/styles';
import PDFViewer from "../pdfViwer/PDFViewer";
import pxToRem from "libs/pxToRem";

interface IDocumentViewer {
  documentUrl: string;
  loading?: boolean;
  hasDocument?: boolean;
  emptyMessage?: string;
  emptyTitle?: string;
}

const DocumentViewer = React.memo(({
  documentUrl,
  loading,
  hasDocument = true,
  emptyMessage = "No hay un documento asociado a esta solicitud.",
  emptyTitle = "Documento no disponible"
}: IDocumentViewer) => {
  const isPdf = documentUrl?.includes(".pdf");
  const [pdfReady, setPdfReady] = useState(false);
  const [imgReady, setImgReady] = useState(false);

  useEffect(() => {
    setPdfReady(false);
    setImgReady(false);
  }, [documentUrl]);

  if (loading) {
    return (
      <DocumentViewerContainer>
        <LoadingContainer>
          <CircularProgress size={60} />
        </LoadingContainer>
      </DocumentViewerContainer>
    );
  }

  if (!hasDocument || !documentUrl) {
    return (
      <DocumentViewerContainer>
        <NoDocumentContainer>
          <DescriptionIcon style={{ fontSize: 80, color: "#9e9e9e" }} />
          <NoDocumentTitle>{emptyTitle}</NoDocumentTitle>
          <NoDocumentSubtitle>
            {emptyMessage}
          </NoDocumentSubtitle>
        </NoDocumentContainer>
      </DocumentViewerContainer>
    );
  }

  const handleOpenNewTab = () => {
    window.open(documentUrl, "_blank");
  };

  const documentReady = isPdf ? pdfReady : imgReady;

  return (
    <DocumentViewerContainer>
      {isPdf ? (
        <PDFViewer url={documentUrl} onLoad={() => setPdfReady(true)} />
      ) : (
        <IMGViewer url={documentUrl} onLoad={() => setImgReady(true)} />
      )}
      {documentReady && (
        <FloatingButton
          onClick={handleOpenNewTab}
          aria-label="Abrir en nueva pestaña"
        >
          <OpenInNewIcon />
        </FloatingButton>
      )}
    </DocumentViewerContainer>
  );
});

interface IIMGViewer {
  url: string;
  onLoad?: () => void;
}

const IMGViewer = ({ url, onLoad }: IIMGViewer) => {
  return (
    <img
      src={url}
      alt="documento"
      style={{ width: pxToRem(743), height: pxToRem(1052) }}
      onLoad={onLoad}
    />
  );
};

const DocumentViewerContainer = styled(MuiBox)({
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  position: "relative",
  minWidth: pxToRem(743),
  minHeight: pxToRem(400),
});

const LoadingContainer = styled(MuiBox)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: pxToRem(743),
  minHeight: pxToRem(600),
});

const FloatingButton = styled(IconButton)({
  position: "sticky",
  top: pxToRem(10),
  alignSelf: "flex-start",
  marginLeft: pxToRem(-50),
  marginRight: pxToRem(10),
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  color: "#fff",
  zIndex: 10,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
});

const NoDocumentContainer = styled(MuiBox)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: pxToRem(60),
  minHeight: pxToRem(400),
  width: pxToRem(743),
});

const NoDocumentTitle = styled(Typography)({
  fontSize: pxToRem(24),
  fontWeight: 600,
  color: "#424242",
  marginTop: pxToRem(20),
  marginBottom: pxToRem(10),
});

const NoDocumentSubtitle = styled(Typography)({
  fontSize: pxToRem(14),
  color: "#757575",
  textAlign: "center",
  maxWidth: pxToRem(400),
  lineHeight: 1.5,
});

export default DocumentViewer;
